import { ListItem } from "@fluidattacks/design";
import { styled } from "styled-components";

import type { IDropDownMap, TVariant } from "./types";

const DropdownContainer = styled.ul<{
  $variant: TVariant;
  $parentMap: IDropDownMap;
}>`
  ${({ theme, $variant, $parentMap }): string => `
  background-color: ${theme.palette.white};
  box-sizing: border-box;
  border: ${
    $variant === "nested-list-item"
      ? "none"
      : `1px solid ${theme.palette.gray[200]}`
  };
  gap: 10px;
  margin-top: ${$variant === "nested-list-item" ? "unset" : "4px !important"};
  max-height: 300px;
  overflow: hidden auto;
  position: ${$variant === "nested-list-item" ? "relative" : "absolute"};
  top: ${$variant === "nested-list-item" ? "0px" : ""};
  width: ${$variant === "nested-list-item" ? "100%" : `${$parentMap.width}`}px;
  border-radius: 4px;
  box-shadow: ${$variant === "nested-list-item" ? "none" : theme.shadows.md};
  z-index: 9999;

  & > li {
    margin: unset;
    padding: ${$variant === "nested-list-item" ? "0px 32px" : "10px 16px"};
  }

  li:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  li:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
  `}
`;

const Container = styled.div<{
  $variant: TVariant;
  $width?: string;
  $error: boolean;
  $open: boolean;
}>`
  ${({ theme, $error, $open, $variant, $width = "240px" }): string => `
  display: ${$variant === "nested-list-item" ? "block" : "flex"};
  flex-direction: column;
  position: static;
  width: ${$width};
  z-index: ${$open ? "50" : "unset"};

  [aria-disabled="true"] {
    pointer-events: none;
    cursor: not-allowed;
  }

  .nested-list-item {
    border: none;
    margin: unset;
  }

  ul {
    display: inline-block;
    margin: unset;
    padding-right: unset;
    padding-left: unset;
  }

  .nested-list-item ul {
    padding: unset;
    margin: unset;
  }

  .parent-option li {
    border: ${
      $variant === "nested-list-item"
        ? "none"
        : `1px solid ${theme.palette.gray["200"]}`
    };
    display: flex;
    border-radius: ${$variant === "nested-list-item" ? "none" : "4px"};
  }

  .parent-option {
    border: ${$error ? `1px solid ${theme.palette.error["500"]}` : "none"};
    border-radius: 4px;
    height: auto;
  }
  `}
`;

const DropDownSelector = styled(ListItem)<{
  disabled: boolean;
  $isInput: boolean;
  $width?: string;
}>`
  overflow: hidden;
  min-width: ${({ $width }): string =>
    $width === undefined ? "" : `${$width} !important`};

  & > div:first-child {
    width: 95%;

    & > p {
      color: ${({ disabled, $isInput }): string =>
        disabled && $isInput ? "inherit" : ""};
      height: 20px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
`;

const ChildrenContainer = styled.div`
  ul {
    flex-direction: column;
  }

  ul,
  ol {
    padding-top: 6px;
    padding-bottom: 6px;
    background-color: #fff;
    position: absolute;
    top: 40px;
    right: 1px;
    z-index: 1;
  }

  .no-hover,
  .user-info {
    text-decoration: none;
    display: flex;
    justify-content: space-between;
    height: auto;
  }

  .user-info,
  .commit-info {
    padding-left: 16px;
    padding-right: 16px;
  }

  .user-info {
    padding-top: 10px;
    padding-bottom: 1px;
  }

  .commit-info {
    padding-top: 1px;
    padding-bottom: 10px;
  }

  li {
    list-style-type: none;
  }

  span {
    white-space: nowrap;
  }

  a {
    color: ${({ theme }): string => theme.palette.gray[800]};
    text-decoration: none;
    padding: unset;
  }

  a:hover {
    text-decoration: none;
    color: ${({ theme }): string => theme.palette.gray[800]};
  }

  #menu-profile-container ul {
    min-width: unset;
    width: auto;
    z-index: 10;
  }

  #menu-profile-container li div {
    min-width: unset;
  }
`;

const OutlineContainer = styled.div<{
  $disabled: boolean;
  $show: boolean;
  $showSuccess?: boolean;
}>`
  align-items: center;
  background: ${({ theme, $disabled }): string =>
    $disabled ? theme.palette.gray[100] : theme.palette.white};
  border: 1px solid;
  border-color: ${({ theme, $show }): string =>
    $show ? theme.palette.error[500] : theme.palette.gray[300]};
  border-radius: ${({ theme }): string => theme.spacing[0.5]};
  color: ${({ theme, $disabled }): string =>
    theme.palette.gray[$disabled ? 500 : 800]};
  display: flex;
  font-family: ${({ theme }): string => theme.typography.type.primary};
  font-size: ${({ theme }): string => theme.typography.text.md};
  font-weight: ${({ theme }): string => theme.typography.weight.regular};
  height: 40px;
  line-height: ${({ theme }): string => theme.spacing[1.25]};
  width: 100%;

  *::placeholder {
    color: ${({ theme }): string => theme.palette.gray[400]};
  }

  span {
    ${({ theme, $disabled }): string =>
      $disabled ? `color: ${theme.palette.gray[400]}` : ""}
  }

  .success-icon {
    display: none;
  }

  &:has(textarea) {
    align-items: start;
    height: auto;
  }

  ${({ theme, $disabled, $show, $showSuccess = false }): string => {
    const successState = $showSuccess
      ? `&:not(:focus-within) { border-color: ${theme.palette.success[500]}; .success-icon { display: block; } }`
      : "";
    const allowHover = $disabled
      ? "input, textarea { cursor: not-allowed }"
      : `&:hover { border-color: ${theme.palette.gray[600]}; }`;

    return $show
      ? ""
      : `
          ${allowHover}
          ${successState}

          &:focus-within {
            border: 2px solid;
            border-color: ${theme.palette.black};

            button.action-btn {
              height: 36px;
              width: 40px;
            }
          }`;
  }}
`;

const OutlineInput = styled.div<{
  $borderRadius?: string;
  $maxWidth?: string;
  $overflowX?: string;
  $width?: string;
}>`
  align-items: center;
  border: transparent;
  border-color: inherit;
  border-right: ${({ $width }): string =>
    $width === undefined ? "" : "inherit"};
  border-radius: ${({ $borderRadius }): string => $borderRadius ?? ""};
  color: inherit;
  display: flex;
  height: 100%;
  gap: 10px;
  justify-content: space-between;
  max-width: ${({ $maxWidth }): string => $maxWidth ?? "unset"};
  overflow-x: ${({ $overflowX }): string => $overflowX ?? "unset"};
  padding: ${({ theme, $overflowX }): string =>
    $overflowX === undefined
      ? `10px ${theme.spacing[0.75]}`
      : `4px ${theme.spacing[0.75]}`};
  width: ${({ $width }): string => $width ?? "100%"};
  white-space: nowrap;

  &:has(textarea) {
    align-items: start;
  }

  ul {
    display: inline-block;
    margin: unset;
    padding-right: unset;
    padding-left: unset;
  }

  .parent-option {
    background-color: inherit;
    display: flex;
    height: 100%;
    margin: unset;
    padding-right: unset;
    padding-left: unset;
    width: 100%;

    li {
      background-color: inherit;
      padding: 0;

      &:hover {
        background-color: ${({ theme }): string => theme.palette.white};
      }
    }
  }
`;

export {
  ChildrenContainer,
  Container,
  DropDownSelector,
  DropdownContainer,
  OutlineContainer,
  OutlineInput,
};
